import {
  deriveInstrumentInfoViewState,
  isInstrumentInfoLoadFailed,
  isInstrumentNotFoundError,
  isInstrumentNotInvitedError,
  isInstrumentPausedError,
} from './instrument-info-state'
import {
  INSTRUMENT_INFO_CAPACITY_MODAL,
  INSTRUMENT_UNAVAILABLE,
} from './instrument-info-messages'
import type {
  InstrumentInfoEntryViewState,
  ProviderInstrumentInfo,
  ProviderInstrumentStatus,
} from './types'

export function resolveInstrumentInfoPageViewState(input: {
  viewStateOverride: InstrumentInfoEntryViewState | null
  infoNotEligible: boolean
  statusNotEligible: boolean
  info: ProviderInstrumentInfo | undefined
  status: ProviderInstrumentStatus | undefined
}): InstrumentInfoEntryViewState {
  if (input.viewStateOverride) {
    return input.viewStateOverride
  }
  if (input.infoNotEligible || input.statusNotEligible) {
    return 'notEligible'
  }
  if (!input.info) {
    return 'error'
  }
  return deriveInstrumentInfoViewState({ info: input.info, status: input.status })
}

export function shouldShowInstrumentInfoUnavailableScreen(input: {
  showNotEligible: boolean
  viewState: InstrumentInfoEntryViewState
  statusQueryError: boolean
  statusQueryPausedError: boolean
  hasInfo: boolean
}): boolean {
  if (input.showNotEligible || input.viewState === 'alreadyCompleted') {
    return false
  }
  if (
    input.viewState === 'paused' ||
    input.viewState === 'closed' ||
    input.viewState === 'capacityReached'
  ) {
    return true
  }
  if (input.statusQueryPausedError) {
    return true
  }
  return input.hasInfo && input.statusQueryError
}

export type InstrumentInfoPagePhase =
  | { kind: 'loading' }
  | { kind: 'notEligible' }
  | { kind: 'notFoundPendingStatus' }
  | { kind: 'redirectHome' }
  | { kind: 'loadError' }
  | { kind: 'completed' }
  | { kind: 'unavailable'; message: string }
  | { kind: 'ready' }

export function resolveInstrumentInfoPagePhase(input: {
  infoLoading: boolean
  infoError: boolean
  infoErrorValue: unknown
  info: ProviderInstrumentInfo | undefined
  statusFetched: boolean
  showNotEligible: boolean
  showUnavailableScreen: boolean
  infoLoadFailed: boolean
  viewState: InstrumentInfoEntryViewState
}): InstrumentInfoPagePhase {
  if (input.infoLoading) {
    return { kind: 'loading' }
  }
  if (input.showNotEligible) {
    return { kind: 'notEligible' }
  }
  if (input.infoError && isInstrumentNotFoundError(input.infoErrorValue)) {
    if (!input.statusFetched) {
      return { kind: 'notFoundPendingStatus' }
    }
    if (input.showUnavailableScreen) {
      return { kind: 'unavailable', message: INSTRUMENT_UNAVAILABLE }
    }
    return { kind: 'redirectHome' }
  }
  if (input.infoLoadFailed || !input.info) {
    return { kind: 'loadError' }
  }
  if (input.viewState === 'alreadyCompleted') {
    return { kind: 'completed' }
  }
  if (input.showUnavailableScreen) {
    const message =
      input.viewState === 'capacityReached'
        ? INSTRUMENT_INFO_CAPACITY_MODAL
        : INSTRUMENT_UNAVAILABLE
    return { kind: 'unavailable', message }
  }
  return { kind: 'ready' }
}

export function isInstrumentInfoNotEligibleError(
  queryError: boolean,
  error: unknown,
): boolean {
  return queryError && isInstrumentNotInvitedError(error)
}

export function isInstrumentStatusPausedError(
  queryError: boolean,
  error: unknown,
): boolean {
  return queryError && isInstrumentPausedError(error)
}

export function isInstrumentInfoLoadFailedState(input: {
  info: ProviderInstrumentInfo | undefined
  infoLoading: boolean
  infoError: boolean
}): boolean {
  return isInstrumentInfoLoadFailed(input)
}
